/**
 * IRR Solver — extracted from model.ts for Phase 15.
 *
 * 3-tier fallback: Newton-Raphson → Secant → Bisection.
 * Pure math, zero dependencies on the model layer.
 */

// ---------- IRR constants ----------
export const IRR_MAX_ITERATIONS = 80;
export const IRR_TOLERANCE = 1e-7;
export const IRR_DEFAULT_GUESS = 0.1;
export const IRR_FALLBACK_ITERATIONS = 40;

export function countCashflowSignChanges(cashflows: number[]): number {
  let signChanges = 0;
  let previousSign = 0;
  for (const cashflow of cashflows) {
    if (cashflow === 0) continue;
    const sign = cashflow > 0 ? 1 : -1;
    if (previousSign !== 0 && sign !== previousSign) signChanges += 1;
    previousSign = sign;
  }
  return signChanges;
}

// ---------- NPV ----------
export function npv(rate: number, cashflows: number[]): number {
  if (!Number.isFinite(rate) || rate <= -1) return Number.NaN;
  let v = 0;
  for (let i = 0; i < cashflows.length; i++)
    v += cashflows[i] / Math.pow(1 + rate, i);
  return v;
}

// ---------- Newton-Raphson ----------
export function irrNewton(
  cashflows: number[],
  guesses: number[],
): number | null {
  for (const g of guesses) {
    let rate = g;
    for (let iter = 0; iter < IRR_MAX_ITERATIONS; iter++) {
      let f = 0,
        df = 0;
      for (let i = 0; i < cashflows.length; i++) {
        const c = cashflows[i];
        const denom = Math.pow(1 + rate, i);
        f += c / denom;
        df += (-i * c) / (denom * (1 + rate));
      }
      if (!Number.isFinite(f) || !Number.isFinite(df) || df === 0) break;
      const next = rate - f / df;
      if (Math.abs(next - rate) < IRR_TOLERANCE) return next;
      if (next <= -0.999 || next > 1e6 || !Number.isFinite(next)) break;
      rate = next;
    }
  }
  return null;
}

// ---------- Secant ----------
export function irrSecant(
  cashflows: number[],
  x0: number,
  x1: number,
): number | null {
  let a = x0,
    b = x1;
  let fa = npv(a, cashflows),
    fb = npv(b, cashflows);
  if (!Number.isFinite(fa) || !Number.isFinite(fb)) return null;
  for (let iter = 0; iter < IRR_FALLBACK_ITERATIONS; iter++) {
    if (Math.abs(fb - fa) < 1e-12) break;
    const c = b - (fb * (b - a)) / (fb - fa);
    if (c <= -0.999 || c > 1e6 || !Number.isFinite(c)) break;
    const fc = npv(c, cashflows);
    if (!Number.isFinite(fc)) break;
    if (Math.abs(fc) < IRR_TOLERANCE) return c;
    a = b;
    fa = fb;
    b = c;
    fb = fc;
  }
  return null;
}

// ---------- Bisection ----------
export function irrBisection(
  cashflows: number[],
  low: number,
  high: number,
): number | null {
  let lo = low,
    hi = high;
  let fLo = npv(lo, cashflows),
    fHi = npv(hi, cashflows);
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi)) return null;
  // Expand bracket until we find a sign change
  for (let expand = 0; expand < 20 && fLo * fHi > 0; expand++) {
    lo -= 0.5;
    hi += 0.5;
    if (lo <= -0.999) lo = -0.99;
    fLo = npv(lo, cashflows);
    fHi = npv(hi, cashflows);
    if (!Number.isFinite(fLo) || !Number.isFinite(fHi)) break;
  }
  if (fLo * fHi > 0) return null;
  for (let iter = 0; iter < IRR_FALLBACK_ITERATIONS; iter++) {
    const mid = (lo + hi) / 2;
    const fMid = npv(mid, cashflows);
    if (!Number.isFinite(fMid)) break;
    if (Math.abs(fMid) < IRR_TOLERANCE) return mid;
    if (fLo * fMid <= 0) {
      hi = mid;
    } else {
      lo = mid;
      fLo = fMid;
    }
  }
  return (lo + hi) / 2;
}

// ---------- 3-tier dispatcher ----------
export function irr(cashflows: number[], guess = IRR_DEFAULT_GUESS): number {
  if (!Array.isArray(cashflows) || cashflows.length < 2) return Number.NaN;
  // IRR undefined when all cashflows have same sign
  const hasPositive = cashflows.some((c) => c > 0);
  const hasNegative = cashflows.some((c) => c < 0);
  if (!hasPositive || !hasNegative) return Number.NaN;

  const guesses = [guess, 0.01, 0.05, 0.1, 0.2, -0.05];
  const nr = irrNewton(cashflows, guesses);
  if (nr !== null) return nr;

  const sec = irrSecant(cashflows, -0.1, 0.3);
  if (sec !== null) return sec;

  const bi = irrBisection(cashflows, -0.5, 2);
  if (bi !== null) return bi;

  return Number.NaN;
}

// Aliases for plan naming convention
export const computeIrr = irr;
export const computeNpv = npv;
